import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import {
  LayoutDashboard,
  FolderKanban,
  Inbox,
  Bell,
  Users,
  Hammer,
  GaugeCircle,
  ClipboardList,
  LogOut,
  Languages,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  CheckCheck,
  BarChart3,
  Wallet,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const navByRole = (role) => {
  const all = {
    dashboard: { to: "/", icon: LayoutDashboard, key: "dashboard" },
    projects: { to: "/projects", icon: FolderKanban, key: "projects" },
    queue: { to: "/queue", icon: Inbox, key: "queue" },
    reports: { to: "/reports", icon: BarChart3, key: "reports" },
    notifications: {
      to: "/notifications",
      icon: Bell,
      key: "notifications",
    },
    correction: {
      to: "/correction",
      icon: ClipboardList,
      key: "correction_journal",
    },
    contractors: { to: "/contractors", icon: Hammer, key: "contractors" },
    users: { to: "/admin/users", icon: Users, key: "users" },
    norms: { to: "/admin/norms", icon: GaugeCircle, key: "norms" },
    audit: { to: "/admin/audit", icon: ClipboardList, key: "audit" },
    tariffs: { to: "/admin/tariffs", icon: Wallet, key: "tariffs" },
  };
  // Superadmin: everything
  if (role === "superadmin")
    return [
      all.dashboard, all.projects, all.queue, all.reports,
      all.contractors, all.users, all.norms, all.tariffs, all.audit, all.notifications,
    ];
  // Contractor: own work only, NO reports, NO users, NO contractors list
  if (role === "contractor")
    return [all.dashboard, all.projects, all.correction, all.notifications];
  // AAYDA Client roles: full visibility incl. reports
  if (role && role.startsWith("aayda_"))
    return [all.dashboard, all.projects, all.reports, all.contractors, all.notifications];
  // Consulting Team Lead: like AAYDA but has queue + correction tools (NO contractors)
  if (role === "consulting_team_lead")
    return [all.dashboard, all.projects, all.queue, all.reports, all.notifications];
  // Other Consulting field specialists: queue-focused, NO reports, NO contractors
  if (role && role.startsWith("consulting_"))
    return [all.dashboard, all.projects, all.queue, all.notifications];
  return [all.dashboard, all.projects, all.notifications];
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let es = null;

    // initial fetch (history)
    api
      .get("/notifications", { params: { unread_only: false } })
      .then((r) => {
        if (!cancelled) setNotifs(r.data);
      })
      .catch(() => {});

    // SSE subscription for real-time push
    const token = localStorage.getItem("ytn_token");
    if (token) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`;
      try {
        es = new EventSource(url);
        es.addEventListener("notification", (e) => {
          try {
            const n = JSON.parse(e.data);
            setNotifs((prev) => {
              if (prev.some((x) => x.id === n.id)) return prev;
              // Toast for high priority
              if (n.priority === "high") {
                const fn =
                  n.category === "error"
                    ? toast.error
                    : n.category === "warning"
                      ? toast.warning
                      : n.category === "success"
                        ? toast.success
                        : toast.info;
                fn(n.title || n.message, {
                  description: n.message !== n.title ? n.message : undefined,
                });
              }
              return [n, ...prev];
            });
          } catch (err) {
            // ignore
          }
        });
        es.onerror = () => {
          // browser will auto-reconnect; do not log spam
        };
      } catch (err) {
        // EventSource not supported — fallback handled below by polling
      }
    }

    // Lightweight polling fallback every 60s in case SSE drops
    const id = setInterval(() => {
      if (cancelled) return;
      api
        .get("/notifications", { params: { unread_only: false } })
        .then((r) => setNotifs(r.data))
        .catch(() => {});
    }, 60000);

    return () => {
      cancelled = true;
      if (es) es.close();
      clearInterval(id);
    };
  }, [user]);

  const unreadCount = notifs.filter((n) => !n.read).length;
  const items = navByRole(user?.role);

  const onNotifClick = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setNotifs((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    if (n.link) nav(n.link);
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setNotifs((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  const categoryConfig = {
    success: { Icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
    error: { Icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
    warning: { Icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
    request: { Icon: Bell, color: "text-[#FF5722]", bg: "bg-orange-50" },
    info: { Icon: Info, color: "text-blue-600", bg: "bg-blue-50" },
  };

  const priorityRank = { high: 0, medium: 1, low: 2 };
  const sortedNotifs = [...notifs].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    const pa = priorityRank[a.priority] ?? 1;
    const pb = priorityRank[b.priority] ?? 1;
    if (pa !== pb) return pa - pb;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      <aside
        className="w-64 bg-[#0A1128] flex flex-col sticky top-0 h-screen text-slate-300"
        data-testid="sidebar"
      >
        <div className="px-5 py-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#E85D2A] flex items-center justify-center glow-accent">
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-black text-[13px] leading-tight tracking-tight text-white truncate">
                Infrastructure
              </div>
              <div className="font-heading font-black text-[13px] leading-tight tracking-tight text-white/70 truncate">
                Supervision & Advisory
              </div>
              <div className="mono text-[9px] tracking-[0.2em] text-[#FF6B35] mt-1 font-bold">
                NƏZARƏT
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto scroll-thin">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "sidebar-active"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <it.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="truncate">{t(it.key)}</span>
              <ChevronRight className="w-3 h-3 ml-auto opacity-30" aria-hidden="true" />
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-left transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-blue-900/40">
                  {user?.full_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {user?.full_name}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {t(`role_label.${user?.role}`)}
                  </div>
                </div>
                <LogOut className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setLang(lang === "az" ? "en" : "az")}
                data-testid="lang-toggle"
              >
                <Languages className="w-4 h-4 mr-2" /> {lang === "az" ? "EN" : "AZ"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  logout();
                  nav("/login");
                }}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-40 flex items-center justify-between px-6">
          <div className="flex items-baseline gap-3">
            <span className="label-overline">Infrastructure Supervision & Advisory</span>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative p-2 rounded-md hover:bg-slate-100"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <Badge
                      className="absolute -top-1 -right-1 h-5 min-w-5 p-0 px-1 bg-[#FF5722] hover:bg-[#FF5722]"
                      data-testid="notif-count"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[420px] max-h-[560px] overflow-y-auto p-0">
                <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-sm">{t("notifications")}</div>
                    <div className="text-[10px] mono text-slate-500">
                      {unreadCount > 0 ? `${unreadCount} oxunmamış` : "Hamısı oxundu"}
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        markAllRead();
                      }}
                      className="text-[10px] mono uppercase text-[#004D99] hover:underline flex items-center gap-1"
                      data-testid="mark-all-read"
                    >
                      <CheckCheck className="w-3 h-3" /> Hamısını oxu
                    </button>
                  )}
                </div>
                {sortedNotifs.length === 0 && (
                  <div className="px-4 py-12 text-center text-sm text-slate-500">
                    <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    {t("no_data")}
                  </div>
                )}
                <div className="divide-y divide-slate-100">
                  {sortedNotifs.map((n) => {
                    const conf = categoryConfig[n.category] || categoryConfig.info;
                    const isHigh = n.priority === "high";
                    return (
                      <button
                        key={n.id}
                        onClick={() => onNotifClick(n)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 ${
                          !n.read ? "bg-blue-50/50" : ""
                        }`}
                        data-testid={`notif-${n.id}`}
                      >
                        <div className={`shrink-0 w-9 h-9 rounded-full ${conf.bg} flex items-center justify-center ${conf.color}`}>
                          <conf.Icon className="w-4 h-4" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className={`text-sm font-semibold truncate ${!n.read ? "text-slate-900" : "text-slate-600"}`}>
                              {n.title || n.message}
                            </div>
                            {isHigh && !n.read && (
                              <span className="shrink-0 w-1.5 h-1.5 bg-[#FF5722] rounded-full" />
                            )}
                          </div>
                          {n.title && n.message !== n.title && (
                            <div className="text-xs text-slate-600 line-clamp-2">
                              {n.message}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] mono text-slate-400">
                              {new Date(n.created_at).toLocaleString("az")}
                            </span>
                            {n.priority && (
                              <span className={`text-[10px] mono px-1.5 py-0.5 rounded uppercase ${
                                n.priority === "high"
                                  ? "bg-red-100 text-red-700"
                                  : n.priority === "medium"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}>
                                {n.priority}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-8 overflow-x-hidden" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
